import { useMemo } from "react";
import Chart from "react-apexcharts";
import { Card, CardContent, Typography, Box, Stack, Chip, Divider } from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";

const MESES = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];

const Tendencia = ({ actual, anterior }) => {
  if (anterior === 0 && actual === 0) return null;
  if (anterior === 0) return <TrendingUpIcon fontSize="small" color="success" />;
  const diff = actual - anterior;
  const pct = Math.abs(Math.round((diff / anterior) * 100));
  if (diff > 0) return (
    <Stack direction="row" spacing={0.3} alignItems="center">
      <TrendingUpIcon fontSize="small" color="success" />
      <Typography variant="caption" color="success.main" fontWeight={600}>+{pct}%</Typography>
    </Stack>
  );
  if (diff < 0) return (
    <Stack direction="row" spacing={0.3} alignItems="center">
      <TrendingDownIcon fontSize="small" color="error" />
      <Typography variant="caption" color="error.main" fontWeight={600}>-{pct}%</Typography>
    </Stack>
  );
  return <TrendingFlatIcon fontSize="small" color="disabled" />;
};

export function GraficoMensual({ data }) {
  const mensualData = useMemo(() => {
    const map = {};
    (data || []).forEach((r) => {
      const mesNum = String(r.mes).padStart(2, '0');
      const key = `${r.ano}-${mesNum}`;
      const label = `${MESES[parseInt(r.mes, 10) - 1]} ${r.ano}`;
      if (!map[key]) map[key] = { key, label, consultas: 0, busquedas: 0, descargas: 0 };
      const cant = parseInt(r.cantidad, 10) || 0;
      if (r.proceso === 'consultas') map[key].consultas += cant;
      else if (r.proceso === 'busquedas') map[key].busquedas += cant;
      else if (r.proceso === 'descargas') map[key].descargas += cant;
    });
    return Object.values(map).sort((a, b) => a.key.localeCompare(b.key));
  }, [data]);

  if (mensualData.length === 0) {
    return (
      <Card sx={{ borderRadius: 3, p: 3 }} elevation={2}>
        <Typography color="text.secondary" align="center">
          Sin datos para mostrar. Realizá una búsqueda primero.
        </Typography>
      </Card>
    );
  }

  const ultimo = mensualData[mensualData.length - 1];
  const penultimo = mensualData.length >= 2 ? mensualData[mensualData.length - 2] : null;

  const chartOptions = {
    chart: {
      type: 'area',
      toolbar: { show: false },
      animations: { enabled: true, speed: 700, animateGradually: { enabled: true, delay: 150 } },
      fontFamily: 'Roboto, sans-serif',
      background: 'transparent',
    },
    stroke: { curve: 'smooth', width: [2.5, 2.5, 2.5] },
    fill: {
      type: 'gradient',
      gradient: { shadeIntensity: 1, opacityFrom: 0.3, opacityTo: 0.02, stops: [0, 95] },
    },
    xaxis: {
      categories: mensualData.map(d => d.label),
      tickAmount: Math.min(mensualData.length, 8),
      labels: {
        rotate: mensualData.length > 6 ? -30 : 0,
        style: { fontSize: '11px', colors: '#888' },
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: { formatter: (v) => Math.round(v), style: { colors: '#888', fontSize: '11px' } },
      min: 0,
    },
    legend: {
      position: 'top',
      horizontalAlign: 'left',
      markers: { radius: 4, width: 10, height: 10 },
      fontSize: '13px',
    },
    colors: ['#1976d2', '#ed6c02', '#2e7d32'],
    tooltip: {
      shared: true,
      intersect: false,
      y: { formatter: (v) => `${v.toLocaleString('es-AR')} usos` },
      theme: 'light',
    },
    dataLabels: { enabled: false },
    grid: {
      borderColor: '#f0f0f0',
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
      padding: { left: 8, right: 8 },
    },
    markers: {
      size: mensualData.length <= 8 ? 4 : 0,
      hover: { size: 6 },
    },
  };

  const series = [
    { name: 'Consultas', data: mensualData.map(d => d.consultas) },
    { name: 'Búsquedas', data: mensualData.map(d => d.busquedas) },
    { name: 'Descargas', data: mensualData.map(d => d.descargas) },
  ];

  return (
    <Card sx={{ borderRadius: 3 }} elevation={2}>
      <CardContent sx={{ pb: '16px !important' }}>
        {/* Header */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2} flexWrap="wrap" gap={1}>
          <Box>
            <Typography variant="h6" fontWeight={700}>Evolución mensual</Typography>
            <Typography variant="body2" color="text.secondary">
              {mensualData.length} período{mensualData.length !== 1 ? 's' : ''} · Comparativo de los 3 procesos
            </Typography>
          </Box>
        </Stack>

        {/* Chart */}
        <Chart options={chartOptions} series={series} type="area" height={300} />

        {/* Resumen último mes */}
        {ultimo && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary" fontWeight={600} mb={1}>
              Último período: {ultimo.label}
            </Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap" gap={1}>
              {[
                { label: 'Consultas', value: ultimo.consultas, prev: penultimo?.consultas ?? 0, color: '#1976d2' },
                { label: 'Búsquedas', value: ultimo.busquedas, prev: penultimo?.busquedas ?? 0, color: '#ed6c02' },
                { label: 'Descargas', value: ultimo.descargas, prev: penultimo?.descargas ?? 0, color: '#2e7d32' },
              ].map(({ label, value, prev, color }) => (
                <Box key={label} sx={{ flex: '1 1 120px', bgcolor: `${color}10`, borderRadius: 2, p: 1.5, minWidth: 100 }}>
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                  <Stack direction="row" spacing={1} alignItems="center" mt={0.25}>
                    <Typography variant="h6" fontWeight={700} color={color}>{value.toLocaleString('es-AR')}</Typography>
                    <Tendencia actual={value} anterior={prev} />
                  </Stack>
                </Box>
              ))}
            </Stack>
          </>
        )}
      </CardContent>
    </Card>
  );
}
